import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Navigation, Footer, WhatsAppButton, CTASection, LOGO_WHITE, HERO_BG, TEAM_IMG } from "@/components/Layout";
import { TrendingUp, Users, Lightbulb, Target, Rocket, Settings, Globe, BarChart3, Zap, CheckCircle2, ArrowRight, ChevronDown } from "lucide-react";

function HeroSection() {
  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* YouTube Video Background */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <iframe
          className="absolute top-1/2 left-1/2 w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          src="https://www.youtube.com/embed/QNXtbsS633w?autoplay=1&mute=1&loop=1&playlist=QNXtbsS633w&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1&enablejsapi=1"
          title="Background Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <div className="animate-fade-in-up">
          <img src="https://customer-assets.emergentagent.com/job_1eac6068-4aef-43b9-95b6-f8db7adb33cf/artifacts/g6qc1ve4_INFINIUM%20LOGO-WHITE.png" alt="Infinium Innovations" className="h-64 md:h-80 w-auto mx-auto mb-6" />
        </div>
        <div className="animate-fade-in-up animation-delay-100">
          <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-cyan-400 backdrop-blur-sm mb-8">
            Strategy & Innovation Consultancy
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 animate-fade-in-up animation-delay-200">
          Transforming Vision<br /><span className="text-gradient">Into Success</span>
        </h1>
        <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-300">
          We help organisations navigate complexity, design new pathways, and deliver real-world impact through strategic innovation.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
          <Link to="/services">
            <Button data-testid="hero-cta-primary" className="btn-gradient text-white rounded-full px-8 py-6 text-lg font-medium">
              Explore Our Work <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
          <Link to="/contact">
            <Button data-testid="hero-cta-secondary" variant="outline" className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20 rounded-full px-8 py-6 text-lg font-medium">
              Get in Touch
            </Button>
          </Link>
        </div>
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 scroll-indicator">
          <ChevronDown className="text-white/60" size={32} />
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section data-testid="stats-section" className="relative py-20 bg-slate-950">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="stats-card bg-slate-900/50 backdrop-blur-sm border-slate-800 rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="flex justify-center mb-4"><div className="service-icon-container"><TrendingUp className="text-cyan-400" size={28} strokeWidth={1.5} /></div></div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">$2.5B+</div>
              <div className="text-slate-400 font-medium">Revenue Generated</div>
            </CardContent>
          </Card>
          <Card className="stats-card bg-slate-900/50 backdrop-blur-sm border-slate-800 rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="flex justify-center mb-4"><div className="service-icon-container"><Users className="text-cyan-400" size={28} strokeWidth={1.5} /></div></div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">500+</div>
              <div className="text-slate-400 font-medium">Clients Served</div>
            </CardContent>
          </Card>
          <Card className="stats-card bg-slate-900/50 backdrop-blur-sm border-slate-800 rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="flex justify-center mb-4"><div className="service-icon-container"><Lightbulb className="text-cyan-400" size={28} strokeWidth={1.5} /></div></div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">98%</div>
              <div className="text-slate-400 font-medium">Client Satisfaction</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function AboutSectionHome() {
  return (
    <section id="about" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://images.unsplash.com/photo-1455849318743-b2233052fcff?auto=format&fit=crop&w=800&q=80" alt="Team collaboration" className="w-full h-auto object-cover" />
            </div>
          </div>
          <div>
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-cyan-50 text-cyan-600 mb-6">About Us</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">Driving Innovation,<br /><span className="text-gradient">Delivering Results</span></h2>
            <p className="text-slate-600 text-lg mb-6 leading-relaxed">Infinium is a strategy and innovation consultancy working with organisations facing complex strategic, operational, and systemic challenges.</p>
            <p className="text-slate-500 mb-8 leading-relaxed">Our team of seasoned professionals brings deep expertise in strategy, technology, and operations. We combine analytical rigor with creative thinking to deliver solutions that drive measurable outcomes.</p>
            <div className="grid grid-cols-2 gap-4 mb-10">
              <div className="flex items-center gap-3"><CheckCircle2 className="text-cyan-500 flex-shrink-0 icon-hover" size={20} /><span className="text-slate-700 font-medium">Data-driven insights</span></div>
              <div className="flex items-center gap-3"><CheckCircle2 className="text-cyan-500 flex-shrink-0 icon-hover" size={20} /><span className="text-slate-700 font-medium">Proven track record</span></div>
              <div className="flex items-center gap-3"><CheckCircle2 className="text-cyan-500 flex-shrink-0 icon-hover" size={20} /><span className="text-slate-700 font-medium">Industry expertise</span></div>
              <div className="flex items-center gap-3"><CheckCircle2 className="text-cyan-500 flex-shrink-0 icon-hover" size={20} /><span className="text-slate-700 font-medium">Tailored solutions</span></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Card className="card-hover border-slate-100">
                <CardContent className="p-6">
                  <div className="service-icon-container mb-4 w-12 h-12"><Target className="text-cyan-500" size={24} strokeWidth={1.5} /></div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Our Mission</h3>
                  <p className="text-slate-500 text-sm">Empower businesses to achieve their full potential through strategic innovation.</p>
                </CardContent>
              </Card>
              <Card className="card-hover border-slate-100">
                <CardContent className="p-6">
                  <div className="service-icon-container mb-4 w-12 h-12"><Rocket className="text-cyan-500" size={24} strokeWidth={1.5} /></div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Our Vision</h3>
                  <p className="text-slate-500 text-sm">Be the catalyst for transformative change in the global business landscape.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesSectionHome() {
  return (
    <section id="services" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-cyan-50 text-cyan-600 mb-6">Our Services</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">What We <span className="text-gradient">Do</span></h2>
          <p className="text-slate-600 text-lg">We support organisations across the full journey from strategic thinking to execution.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card className="card-hover bg-white border-slate-100 rounded-2xl overflow-hidden">
            <CardContent className="p-8">
              <div className="service-icon-container mb-6"><Target className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Strategy</h3>
              <p className="text-slate-500 mb-6">Helping organisations define direction, make trade-offs, and prioritise what matters most.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Corporate strategy</li>
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Growth & market entry</li>
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Operating model design</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="card-hover bg-white border-slate-100 rounded-2xl overflow-hidden">
            <CardContent className="p-8">
              <div className="service-icon-container mb-6"><Lightbulb className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Innovation & New Ventures</h3>
              <p className="text-slate-500 mb-6">Designing and launching new ideas that are grounded in reality.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Innovation strategy</li>
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Business model design</li>
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Pilot & scale-up</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="card-hover bg-white border-slate-100 rounded-2xl overflow-hidden">
            <CardContent className="p-8">
              <div className="service-icon-container mb-6"><Settings className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Transformation & Delivery</h3>
              <p className="text-slate-500 mb-6">Supporting execution of complex initiatives from concept through delivery.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Transformation roadmaps</li>
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Change management</li>
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Performance tracking</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="card-hover bg-white border-slate-100 rounded-2xl overflow-hidden">
            <CardContent className="p-8">
              <div className="service-icon-container mb-6"><Globe className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Systems & Infrastructure</h3>
              <p className="text-slate-500 mb-6">Working across sectors to address interconnected challenges.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Infrastructure strategy</li>
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Sustainability integration</li>
                <li className="flex items-center gap-2 text-slate-600"><div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />Multi-stakeholder models</li>
              </ul>
            </CardContent>
          </Card>
        </div>
        <div className="text-center mt-12">
          <Link to="/services"><Button className="btn-gradient text-white rounded-full px-8 py-3 font-medium">View All Services <ArrowRight className="ml-2" size={18} /></Button></Link>
        </div>
      </div>
    </section>
  );
}

function ApproachSectionHome() {
  return (
    <section id="approach" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-cyan-50 text-cyan-600 mb-6">Our Approach</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">How We <span className="text-gradient">Work</span></h2>
            <p className="text-slate-600 text-lg mb-10 leading-relaxed">We partner with organisations navigating complex strategic and systemic challenges. Our approach balances strategic clarity with practical delivery.</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 card-hover"><div className="service-icon-container w-10 h-10"><Zap className="text-cyan-500" size={20} strokeWidth={1.5} /></div><span className="text-slate-700 font-medium">Collaborative</span></div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 card-hover"><div className="service-icon-container w-10 h-10"><BarChart3 className="text-cyan-500" size={20} strokeWidth={1.5} /></div><span className="text-slate-700 font-medium">Structured</span></div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 card-hover"><div className="service-icon-container w-10 h-10"><Target className="text-cyan-500" size={20} strokeWidth={1.5} /></div><span className="text-slate-700 font-medium">Pragmatic</span></div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 card-hover"><div className="service-icon-container w-10 h-10"><CheckCircle2 className="text-cyan-500" size={20} strokeWidth={1.5} /></div><span className="text-slate-700 font-medium">Outcome-driven</span></div>
            </div>
          </div>
          <div>
            <div className="space-y-6">
              <div className="approach-step pl-6 py-4"><div className="flex items-start gap-4"><span className="text-gradient text-2xl font-bold">01</span><div><h3 className="text-xl font-semibold text-slate-900 mb-1">Understand</h3><p className="text-slate-500">Context, constraints, and stakeholder dynamics</p></div></div></div>
              <div className="approach-step pl-6 py-4"><div className="flex items-start gap-4"><span className="text-gradient text-2xl font-bold">02</span><div><h3 className="text-xl font-semibold text-slate-900 mb-1">Frame</h3><p className="text-slate-500">Define the problem and success criteria</p></div></div></div>
              <div className="approach-step pl-6 py-4"><div className="flex items-start gap-4"><span className="text-gradient text-2xl font-bold">03</span><div><h3 className="text-xl font-semibold text-slate-900 mb-1">Design</h3><p className="text-slate-500">Develop options and pathways</p></div></div></div>
              <div className="approach-step pl-6 py-4"><div className="flex items-start gap-4"><span className="text-gradient text-2xl font-bold">04</span><div><h3 className="text-xl font-semibold text-slate-900 mb-1">Decide</h3><p className="text-slate-500">Support informed decision-making</p></div></div></div>
              <div className="approach-step pl-6 py-4"><div className="flex items-start gap-4"><span className="text-gradient text-2xl font-bold">05</span><div><h3 className="text-xl font-semibold text-slate-900 mb-1">Deliver</h3><p className="text-slate-500">Enable execution and follow-through</p></div></div></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    function handleScroll() { setIsScrolled(window.scrollY > 50); }
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div data-testid="homepage" className="min-h-screen">
      <Navigation isScrolled={isScrolled} />
      <HeroSection />
      <AboutSectionHome />
      <ServicesSectionHome />
      <ApproachSectionHome />
      <CTASection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
